import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useColorMode } from '@/hooks';
import { ConfirmMessage } from './ConfirmMessage';
import type { ConfirmSeverity } from './types';

type Props = {
    open: boolean;
    title?: React.ReactNode;
    message?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    severity?: ConfirmSeverity;
    pending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const severityVariant: Record<ConfirmSeverity, 'destructive' | 'warning' | 'contained'> = {
    danger: 'destructive',
    warning: 'warning',
    info: 'contained',
};

export const ConfirmationModal = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    severity = 'danger',
    pending,
    onConfirm,
    onCancel,
}: Props) => {
    const { t } = useTranslation();
    const isDarkMode = useColorMode();

    return (
        <Dialog
            open={open}
            onClose={pending ? undefined : onCancel}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 3,
                    borderRadius: '12px',
                    backgroundColor: theme => (isDarkMode ? theme.custom.c37 : theme.custom.c1),
                    color: isDarkMode ? theme => theme.custom.c1 : undefined,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: theme => theme.custom.c69,
                    fontSize: 24,
                    lineHeight: 1,
                    fontWeight: 500,
                    p: 0,
                }}
            >
                {title ?? t('want-delete')}
                <IconButton onClick={onCancel} disabled={pending} sx={{ p: 0, color: theme => theme.custom.c45 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            {message != null && (
                <DialogContent sx={{ p: 0, overflow: 'visible' }}>
                    <ConfirmMessage>{message}</ConfirmMessage>
                </DialogContent>
            )}
            <DialogActions sx={{ p: 0, flexDirection: 'column', gap: 0.5, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
                <Button onClick={onConfirm} variant={severityVariant[severity]} disabled={pending} className="w-full rounded-lg">
                    {pending ? <Spinner size={20} /> : (confirmLabel ?? t('delete'))}
                </Button>
                <Button onClick={onCancel} variant="outlined" disabled={pending} className="w-full rounded-lg dark:text-c4">
                    {cancelLabel ?? t('cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
